import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import APPrintablePage from "../../../../components/APPrintablePage";
import APCalculation from "../../../../components/calculations/APCalculation";
import APCalculationInput from "../../../../components/calculations/APCalculationInput";
import APCalculationOutput from "../../../../components/calculations/APCalculationOutput";
import APPeriodicCalculations from "../../../../components/calculations/APPeriodicCalculation";
import { toDollars } from "../../../../helpers/currencies";
import { QuoteCalculations } from "../../../../types/Calculation";

type DisplayedFields = {
  startDate?: boolean;
  termMths?: boolean;
  termYrs?: boolean;

  annualRentalsIncGstLcc?: boolean;
  annualCeLcc?: boolean;
  annualLcc?: boolean;
  grossCost?: boolean;

  salaryExSuper?: boolean;
  salaryExSuperIncomeTax?: boolean;
  netSalary?: boolean;
  netSalaryNoPackaging?: boolean;

  preTaxRunningCostDeductions: boolean;
  preTaxSacrifice?: boolean;
  salaryExPreTaxSacrifice?: boolean;
  salaryExPreTaxSacrificeIncomeTax?: boolean;
  annualEmployeeContribution?: boolean;
  netSalaryWithPackaging?: boolean;

  netCost?: boolean;
  savings?: boolean;

  runningCosts?: boolean;
  savingsPercentage?: boolean;
};

export const SALARY_COMPARISON_ALL_FIELDS: DisplayedFields = {
  startDate: true,
  termMths: true,
  termYrs: true,

  annualRentalsIncGstLcc: true,
  annualCeLcc: true,
  annualLcc: true,
  grossCost: true,
  runningCosts: true,

  salaryExSuper: true,
  salaryExSuperIncomeTax: true,
  netSalary: true,
  netSalaryNoPackaging: true,

  preTaxRunningCostDeductions: true,
  preTaxSacrifice: true,
  salaryExPreTaxSacrifice: true,
  salaryExPreTaxSacrificeIncomeTax: true,
  annualEmployeeContribution: true,
  netSalaryWithPackaging: true,

  netCost: true,
  savings: true,

  savingsPercentage: true,
};

const SalaryComparisonCalculations = ({
  calculations,
  displayedFields,
}: {
  calculations: QuoteCalculations;
  displayedFields: DisplayedFields;
}) => {
  const { inputs, salaryComparison, luxuryLease } = calculations;

  return (
    <APPrintablePage
      topContent={
        <Stack spacing={2}>
          <Box>
            {displayedFields.startDate && (
              <APCalculation name="Start Date" value={inputs.startDate} />
            )}
            {displayedFields.termMths && (
              <APCalculationInput name="Term Months" value={inputs.termMths} />
            )}
            {displayedFields.termYrs && (
              <APCalculationInput name="Term Years" value={inputs.termYrs} />
            )}
          </Box>

          <Box>
            <Typography variant="titleLarge">Gross Cost</Typography>
            {displayedFields.annualRentalsIncGstLcc && (
              <APCalculationInput
                name="Rentals Inc GST & LCC"
                value={inputs.rentalsIncGstLcc.annual}
                currency
              />
            )}

            {displayedFields.annualCeLcc && (
              <APCalculationInput
                name="Catch-e LCC"
                value={inputs.lcc.annual}
                currency
              />
            )}

            {displayedFields.annualLcc && (
              <APCalculationInput
                name="Auto-UX LCC"
                value={luxuryLease.luxuryCarCharge.result.annual}
                currency
              />
            )}

            {displayedFields.grossCost && (
              <APCalculation
                name={salaryComparison.grossCost.name}
                value={toDollars(salaryComparison.grossCost.result.annual, 2)}
                tooltip={salaryComparison.grossCost.formula}
              />
            )}

            {displayedFields.runningCosts && (
              <APCalculation
                name={salaryComparison.runningCosts.name}
                value={toDollars(
                  salaryComparison.runningCosts.result.annual,
                  2
                )}
                tooltip={salaryComparison.runningCosts.formula}
              />
            )}
          </Box>

          <Box>
            <Typography variant="titleLarge">Net Salary</Typography>
            {displayedFields.salaryExSuper && (
              <APCalculationInput
                name="Salary Ex Super"
                value={inputs.salaryExSuper}
                currency
              />
            )}
            {displayedFields.salaryExSuperIncomeTax && (
              <APCalculationOutput
                calculation={salaryComparison.salaryExSuperIncomeTax}
                currency
              />
            )}
            {displayedFields.netSalary && (
              <APCalculationOutput
                calculation={salaryComparison.netSalary}
                currency
              />
            )}
            {displayedFields.netSalaryNoPackaging && (
              <APCalculationOutput
                calculation={salaryComparison.netSalaryNoPackaging}
                currency
              />
            )}
          </Box>

          <Box>
            <Typography variant="titleLarge">
              Net Salary (With Packaging)
            </Typography>
            {displayedFields.preTaxRunningCostDeductions && (
              <APCalculationInput
                name="Pre Tax Running Cost Deductions"
                value={inputs.preTaxRunningCostDeductions.annual}
                currency
              />
            )}

            {displayedFields.preTaxSacrifice && (
              <APCalculationOutput
                calculation={salaryComparison.preTaxSacrifice}
                currency
              />
            )}

            {displayedFields.salaryExPreTaxSacrifice && (
              <APCalculationOutput
                calculation={salaryComparison.salaryExPreTaxSacrifice}
                currency
              />
            )}

            {displayedFields.salaryExPreTaxSacrificeIncomeTax && (
              <APCalculationOutput
                calculation={salaryComparison.salaryExPreTaxSacrificeIncomeTax}
                currency
              />
            )}

            {displayedFields.annualEmployeeContribution && (
              <APCalculationInput
                name="Employee Contribution"
                value={inputs.employeeContribution.annual}
                currency
              />
            )}

            {displayedFields.netSalaryWithPackaging && (
              <APCalculationOutput
                calculation={salaryComparison.netSalaryWithPackaging}
                currency
              />
            )}
          </Box>

          <Box>
            <Typography variant="titleLarge">Savings</Typography>

            {displayedFields.netCost && (
              <APCalculation
                name={salaryComparison.netCost.name}
                value={toDollars(salaryComparison.netCost.result.annual, 2)}
                tooltip={salaryComparison.netCost.formula}
              />
            )}
            {displayedFields.savings && (
              <APCalculation
                name={salaryComparison.savings.name}
                value={toDollars(salaryComparison.savings.result.annual, 2)}
                tooltip={salaryComparison.savings.formula}
              />
            )}
            {displayedFields.savingsPercentage && (
              <APCalculation
                name={salaryComparison.savingsPercentage.name}
                value={`${salaryComparison.savingsPercentage.result}%`}
                tooltip={salaryComparison.savingsPercentage.formula}
              />
            )}
          </Box>
        </Stack>
      }
      bottomContent={
        <Stack spacing={4}>
          {displayedFields.savings && (
            <APPeriodicCalculations
              calculations={[
                {
                  name: salaryComparison.grossCost.name,
                  amounts: salaryComparison.grossCost.result,
                },
                {
                  name: salaryComparison.netCost.name,
                  amounts: salaryComparison.netCost.result,
                },
                {
                  name: salaryComparison.savings.name,
                  amounts: salaryComparison.savings.result,
                },
              ]}
            />
          )}
        </Stack>
      }
    />
  );
};

export default SalaryComparisonCalculations;
